import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
    Box,
    Text,
    Spinner,
    Alert,
    AlertIcon,
    VStack,
    useColorModeValue} from '@chakra-ui/react';
import { FiUpload } from 'react-icons/fi';
import { useEncryption } from 'contexts/EncryptionContext';
import axios from 'axios';
import env from 'config/env';
import JournalImportPreview from './JournalImportPreview';
import { useJournal } from 'contexts/JournalContext';
import { useTags } from 'contexts/TagsContext';
import { useCustomFields } from 'contexts/CustomFieldsContext';

interface JournalImportProps {
    onSuccess?: () => void;
}

const JournalImport: React.FC<JournalImportProps> = ({ onSuccess }) => {
    const { encrypt } = useEncryption();
    const { refreshEntries } = useJournal();
    const { refreshTags } = useTags();
    const { fetchCustomFields } = useCustomFields();
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [preview, setPreview] = useState<any>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        setUploading(true);
        setError(null);
        setSuccess(null);
        setPreview(null);

        try {
            const formData = new FormData();
            acceptedFiles.forEach(file => {
                formData.append('files', file);
            });

            // Get master key from session storage (this was stored during login)
            const masterKey = localStorage.getItem('masterKey');
            if (!masterKey) {
                throw new Error('Encryption key not found');
            }

            // First get the preview
            const response = await axios.post(`${env.API_URL}/journal/import/preview`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });

            setPreview(response.data);
            setIsPreviewOpen(true);
        } catch (err) {
            if (axios.isAxiosError(err)) {
                setError(err.response?.data?.message || 'Error importing journal entries');
            } else {
                setError('Error importing journal entries');
            }
        } finally {
            setUploading(false);
        }
    }, []);

    const handleImport = async (previewData: any) => {
        try {
            // Encrypt all the entries
            const encryptedEntries = await Promise.all(previewData.entries.map(async (entry: any) => ({
                encrypted_content: await encrypt(entry.content),
                encrypted_mood_label: entry.mood_label ? await encrypt(entry.mood_label) : null,
                entry_date: entry.entry_date,
                tags: entry.tags || [],
                custom_fields: await Promise.all((entry.custom_fields || []).map(async (field: any) => ({
                    name: field.name,
                    encrypted_value: await encrypt(field.value),
                    field_type: field.field_type,
                    category: field.category,
                    unit: field.unit,
                    target_value: field.target_value,
                    frequency: field.frequency
                })))
            })));

            // Encrypt tag names
            const encryptedTags = await Promise.all(previewData.stats.tags.map(async (tag: any) => ({
                ...tag,
                name: tag.name,
                encrypted_name: await encrypt(tag.name),
                color: tag.color
            })));

            // Encrypt custom field names
            const encryptedCustomFields = await Promise.all(previewData.stats.customFields.map(async (field: any) => ({
                ...field,
                name: field.name,
                encrypted_name: await encrypt(field.name),
                field_type: field.field_type,
                category: field.category,
                unit: field.unit,
                target_value: field.target_value,
                frequency: field.frequency,
                show_in_new_entries: field.show_in_new_entries,
                is_archived: field.is_archived
            })));

            // Send the encrypted data for bulk import
            const response = await axios.post(`${env.API_URL}/journal/import/bulk`, {
                entries: encryptedEntries,
                customFields: encryptedCustomFields,
                tags: encryptedTags
            }, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });

            // Refresh all data
            await Promise.all([
                refreshEntries(),
                refreshTags(),
                fetchCustomFields()
            ]);

            setSuccess(`Successfully imported ${response.data.count} journal entries`);
            setIsPreviewOpen(false);
            onSuccess?.();
        } catch (error) {
            console.error('Error importing entries:', error);
            setError('Failed to import entries. Please try again.');
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'text/plain': ['.txt'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            'application/json': ['.json'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'text/csv': ['.csv']
        },
        multiple: true
    });

    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const hoverBg = useColorModeValue('gray.50', 'gray.700');

    return (
        <>
            <VStack spacing={4} align="stretch" p={4}>
                <Box
                    {...getRootProps()}
                    borderWidth={2}
                    borderStyle="dashed"
                    borderColor={isDragActive ? 'blue.500' : borderColor}
                    borderRadius="md"
                    p={6}
                    textAlign="center"
                    cursor="pointer"
                    bg={isDragActive ? hoverBg : 'transparent'}
                    _hover={{ bg: hoverBg }}
                    transition="all 0.2s"
                >
                    <input {...getInputProps()} />
                    <FiUpload size={48} style={{ margin: '0 auto 16px' }} />
                    <Text>
                        {isDragActive
                            ? 'Drop the files here...'
                            : 'Drag and drop files here, or click to select files'}
                    </Text>
                    <Text fontSize="sm" color="gray.500" mt={2}>
                        Supported formats: .txt, .docx, .json, .xlsx, .csv
                    </Text>
                </Box>

                {uploading && (
                    <Box textAlign="center" py={4}>
                        <Spinner size="lg" />
                    </Box>
                )}

                {error && (
                    <Alert status="error">
                        <AlertIcon />
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert status="success">
                        <AlertIcon />
                        {success}
                    </Alert>
                )}
            </VStack>

            {preview && (
                <JournalImportPreview
                    isOpen={isPreviewOpen}
                    onClose={() => setIsPreviewOpen(false)}
                    preview={preview}
                    onConfirm={handleImport}
                />
            )}
        </>
    );
};

export default JournalImport; 